/**
 * useRoutines / useRoutineDetail (SYN-1506) — fetch the merged routines
 * composition from Command's `GET /api/routines[/:id]` BFF routes (spec
 * §7.3/§7.4). Mirrors useListings/useHome: Clerk JWT auth, role-context
 * header, fail-closed parsing. The `tier` on each item is BFF-computed (spec
 * §9) and consumed as-is — never re-derived client-side.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { createApiHeaders } from "../lib/api";
import { fetchWithBackoff } from "../lib/rate-limit-backoff";
import {
  normalizeRoutineItem,
  parseRoutinesPayload,
  type RoutineItem,
  type RoutinesPayload,
} from "../lib/routines";
import { parseArtifactAccess, type ArtifactAccess } from "../lib/artifact-access";
import { useRoleContext } from "../providers/RoleProvider";
import { useOrgContext } from "../providers/OrgProvider";

const ROUTINES_API = "/api/routines";

export type RoutinesStatus =
  | "loading"
  | "ready"
  | "empty"
  | "unauthenticated"
  | "forbidden"
  | "unavailable";

export interface UseRoutinesResult {
  items: RoutineItem[];
  /** Per-section error codes from the BFF (`access` | `unavailable` | null). */
  errors: RoutinesPayload["errors"];
  status: RoutinesStatus;
  error: string | null;
  refetch: () => Promise<void>;
}

const NO_ERRORS: RoutinesPayload["errors"] = { listings: null, readiness: null };

/**
 * Contracts-facet scope (SYN-2104, catalog IA addendum §3) — `mine` | `org`,
 * forwarded as `?scope=` so the BFF filters server-side (routines.ts). Omit
 * to keep today's unfiltered behavior (the legacy /routines page).
 */
export type RoutineScope = "mine" | "org";

function useRoutinesHeaders() {
  const { getToken, userId, orgId } = useAuth();
  const { currentOrg } = useOrgContext();
  const { currentRole, isAllRolesView } = useRoleContext();
  return useCallback(async () => {
    const token = await getToken({ template: "command-session" });
    const headers = createApiHeaders({
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    });
    if (!isAllRolesView && currentRole?.id) {
      headers.set("X-Role-Context", currentRole.id);
    } else {
      headers.delete("X-Role-Context");
    }
    return headers;
  }, [currentRole?.id, getToken, isAllRolesView, userId, orgId, currentOrg?.orgId]);
}

// One home for the save signal (027.006), so hooks that do not depend on this module can use it.
import { ROUTINES_CHANGED_EVENT } from "../lib/routines-changed";
export { ROUTINES_CHANGED_EVENT };

export function useRoutines(scope?: RoutineScope, autoRefresh = false): UseRoutinesResult {
  const buildHeaders = useRoutinesHeaders();
  const [items, setItems] = useState<RoutineItem[]>([]);
  const [errors, setErrors] = useState<RoutinesPayload["errors"]>(NO_ERRORS);
  const [status, setStatus] = useState<RoutinesStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const requestGeneration = useRef(0);
  const loadedContext = useRef<
    { scope: typeof scope; buildHeaders: typeof buildHeaders } | undefined
  >(undefined);
  const activeContext = useRef({ scope, buildHeaders });
  activeContext.current = { scope, buildHeaders };

  const fetchRoutines = useCallback(
    async (quiet = false) => {
      if (
        activeContext.current.scope !== scope ||
        activeContext.current.buildHeaders !== buildHeaders
      ) {
        return;
      }
      const requestId = ++requestGeneration.current;
      const isCurrentRequest = () =>
        activeContext.current.scope === scope &&
        activeContext.current.buildHeaders === buildHeaders &&
        requestId === requestGeneration.current;
      if (!quiet) setStatus("loading");
      setError(null);
      try {
        const url = scope ? `${ROUTINES_API}?scope=${scope}` : ROUTINES_API;
        const headers = await buildHeaders();
        if (!isCurrentRequest()) return;
        const res = await fetchWithBackoff(url, { headers });
        if (!isCurrentRequest()) return;
        if (res.status === 401 || res.status === 403) {
          setItems([]);
          setStatus(res.status === 401 ? "unauthenticated" : "forbidden");
          return;
        }
        if (!res.ok) {
          setItems([]);
          setStatus("unavailable");
          setError(`Routines service error (${res.status})`);
          return;
        }
        const payload = parseRoutinesPayload(await res.json().catch(() => null));
        if (!isCurrentRequest()) return;
        loadedContext.current = { scope, buildHeaders };
        setItems(payload.items);
        setErrors(payload.errors);
        setStatus(payload.items.length === 0 ? "empty" : "ready");
      } catch (err) {
        if (!isCurrentRequest()) return;
        setItems([]);
        setStatus("unavailable");
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [buildHeaders, scope],
  );

  const activeFetch = useRef(fetchRoutines);
  activeFetch.current = fetchRoutines;
  const refetch = useCallback(() => activeFetch.current(), []);

  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const refresh = async (quiet = false) => {
      if (!quiet || document.visibilityState !== "hidden") await fetchRoutines(quiet);
      if (live && autoRefresh) timer = setTimeout(() => void refresh(true), 15_000);
    };
    void refresh();
    // Story 027.006: a durable routine save in chat asks every routines surface to re-read
    // from the API now, rather than waiting for the next poll. It carries no data.
    const onChanged = () => void fetchRoutines(true);
    window.addEventListener(ROUTINES_CHANGED_EVENT, onChanged);
    return () => {
      live = false;
      clearTimeout(timer);
      window.removeEventListener(ROUTINES_CHANGED_EVENT, onChanged);
      requestGeneration.current += 1;
    };
  }, [fetchRoutines, autoRefresh]);

  const belongsToContext =
    loadedContext.current?.scope === scope && loadedContext.current?.buildHeaders === buildHeaders;
  return { items: belongsToContext ? items : [], errors, status, error, refetch };
}

export type RoutineDetailStatus =
  | "loading"
  | "ready"
  | "not_found"
  | "unauthenticated"
  | "forbidden"
  | "unavailable";

export interface UseRoutineDetailResult {
  item: RoutineItem | null;
  status: RoutineDetailStatus;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useRoutineDetail(id: string | undefined): UseRoutineDetailResult {
  const buildHeaders = useRoutinesHeaders();
  const [item, setItem] = useState<RoutineItem | null>(null);
  const [status, setStatus] = useState<RoutineDetailStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!id) {
      setItem(null);
      setStatus("not_found");
      return;
    }
    setStatus("loading");
    setError(null);
    try {
      const res = await fetchWithBackoff(`${ROUTINES_API}/${encodeURIComponent(id)}`, {
        headers: await buildHeaders(),
      });
      if (res.status === 401 || res.status === 403) {
        setItem(null);
        setStatus(res.status === 401 ? "unauthenticated" : "forbidden");
        return;
      }
      if (res.status === 404) {
        setItem(null);
        setStatus("not_found");
        return;
      }
      if (!res.ok) {
        setItem(null);
        setStatus("unavailable");
        setError(`Routines service error (${res.status})`);
        return;
      }
      const data = (await res.json().catch(() => null)) as { item?: unknown } | null;
      const parsed = data?.item ? normalizeRoutineItem(data.item) : undefined;
      if (!parsed) {
        setItem(null);
        setStatus("not_found");
        return;
      }
      setItem(parsed);
      setStatus("ready");
    } catch (err) {
      setItem(null);
      setStatus("unavailable");
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [buildHeaders, id]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { item, status, error, refetch };
}

// ---------------------------------------------------------------------------
// Request access (SYN-1519, spec §7.3)
// ---------------------------------------------------------------------------

export type RequestAccessStatus = "idle" | "submitting" | "requested" | "error";

export interface UseRequestAccessResult {
  status: RequestAccessStatus;
  /** Item id currently being submitted (for per-card spinners). */
  pendingId: string | null;
  error: string | null;
  /**
   * POST /api/routines/:id/request-access. Resolves true when the request is
   * recorded server-side (201, or 409 = one is already pending — same end
   * state). NO optimistic identity state: callers refetch the routines
   * composition so chips render from Atlas truth.
   */
  requestAccess: (id: string) => Promise<boolean>;
  reset: () => void;
}

export function useRequestAccess(): UseRequestAccessResult {
  const buildHeaders = useRoutinesHeaders();
  const [status, setStatus] = useState<RequestAccessStatus>("idle");
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const requestAccess = useCallback(
    async (id: string) => {
      setStatus("submitting");
      setPendingId(id);
      setError(null);
      try {
        const res = await fetch(`${ROUTINES_API}/${encodeURIComponent(id)}/request-access`, {
          method: "POST",
          headers: await buildHeaders(),
        });
        // 409 access_request_pending is success-shaped: the request exists.
        if (res.status === 201 || res.status === 409) {
          setStatus("requested");
          return true;
        }
        setStatus("error");
        setError(
          res.status === 401 || res.status === 403
            ? "You're signed out or not allowed to request this."
            : "The request couldn't be sent. Try again in a moment.",
        );
        return false;
      } catch {
        setStatus("error");
        setError("The request couldn't be sent. Try again in a moment.");
        return false;
      } finally {
        setPendingId(null);
      }
    },
    [buildHeaders],
  );

  const reset = useCallback(() => {
    setStatus("idle");
    setPendingId(null);
    setError(null);
  }, []);

  return { status, pendingId, error, requestAccess, reset };
}

// ---------------------------------------------------------------------------
// Grab (SYN-1903, spec §4.3/§4.4) — member self-adopt of a COMMUNITY item.
// ---------------------------------------------------------------------------

export type GrabStatus = "idle" | "submitting" | "grabbed" | "error";

export interface UseGrabRoutineResult {
  status: GrabStatus;
  /** Item id currently being submitted (for per-card spinners). */
  pendingId: string | null;
  error: string | null;
  /**
   * POST /api/routines/:id/grab. Resolves with the post-grab ArtifactAccess on
   * success (202 grabbed or 409 already_grabbed — both carry { artifactAccess }
   * in the BFF response). Returns null on failure (403 not community-scoped, 501
   * grab not yet implemented, or any network error). NO optimistic identity
   * state: callers refetch so the item flips to accessState:"yours" from truth.
   * The returned ArtifactAccess is used by RoutineCard to drive the post-grab
   * action (run vs navigate-to-detail) from fresh server state, not the pre-grab
   * closure.
   */
  grab: (id: string) => Promise<ArtifactAccess | null>;
  reset: () => void;
}

export function useGrabRoutine(): UseGrabRoutineResult {
  const buildHeaders = useRoutinesHeaders();
  const [status, setStatus] = useState<GrabStatus>("idle");
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const grab = useCallback(
    async (id: string) => {
      setStatus("submitting");
      setPendingId(id);
      setError(null);
      try {
        const res = await fetch(`${ROUTINES_API}/${encodeURIComponent(id)}/grab`, {
          method: "POST",
          headers: await buildHeaders(),
        });
        // 202 grabbed and 409 already_grabbed are both success-shaped: the item
        // is the caller's. Parse the BFF's artifactAccess from the response so
        // the caller gets fresh post-grab actions (not the pre-grab closure).
        if (res.status === 202 || res.status === 409) {
          setStatus("grabbed");
          const body = (await res.json().catch(() => null)) as {
            artifactAccess?: unknown;
          } | null;
          return parseArtifactAccess(body?.artifactAccess) ?? null;
        }
        setStatus("error");
        // Prefer the BFF's plain-language message (403/501 carry one) so the
        // user sees why; fall back to a generic line.
        const body = (await res.json().catch(() => null)) as { message?: unknown } | null;
        setError(
          typeof body?.message === "string" && body.message.length > 0
            ? body.message
            : res.status === 401
              ? "You're signed out. Sign in and try again."
              : "We couldn't grab this routine. Try again in a moment.",
        );
        return null;
      } catch {
        setStatus("error");
        setError("We couldn't grab this routine. Try again in a moment.");
        return null;
      } finally {
        setPendingId(null);
      }
    },
    [buildHeaders],
  );

  const reset = useCallback(() => {
    setStatus("idle");
    setPendingId(null);
    setError(null);
  }, []);

  return { status, pendingId, error, grab, reset };
}
